// src/AccessSessionId.ts
import { isStringGuard } from "@crossingminds/utils";
var AccessSessionId = class {
  #localStorageKey;
  #sessionId;
  constructor({ localStorageKey }) {
    if (!(typeof globalThis === "object" && typeof globalThis?.crypto?.randomUUID === "function" && typeof globalThis?.localStorage?.getItem === "function")) {
      console.error(
        "Could not create AccessSessionId instance. Recommendations will not work as expected. `globalThis`, `crypto`, and `localStorage` must be available."
      );
    }
    this.#localStorageKey = localStorageKey;
    this.#sessionId = this.#getOrCreateSessionId();
  }
  get value() {
    return this.#sessionId;
  }
  valueOf = () => this.#sessionId;
  toString = () => this.#sessionId;
  #getOrCreateSessionId = () => {
    if (typeof globalThis === "object" && typeof globalThis?.crypto?.randomUUID === "function") {
      const localId = localStorage.getItem(this.#localStorageKey);
      if (isStringGuard(localId)) {
        return localId;
      }
      if (typeof globalThis?.crypto?.randomUUID === "function") {
        const newId = globalThis.crypto.randomUUID();
        localStorage.setItem(this.#localStorageKey, newId);
        return newId;
      }
    }
    return "";
  };
};

// src/getItemBasedRecommendations.ts
import { GetRelatedItemRecommendationsCommand } from "@crossingminds/recommendation-client";
import { run as run2 } from "@crossingminds/utils";

// src/createAuthenticatedRecommendationClient.ts
import md5 from "md5";
import {
  minutesToMilliseconds,
  r,
  run,
  isAccurateDateAvailable,
  secondsToMilliseconds
} from "@crossingminds/utils";
import {
  LoginServiceCommand,
  RecommendationClient
} from "@crossingminds/recommendation-client";

// src/OptimizedInputProperties.ts
var MEMORY_CACHE_ONLY = Symbol("memory-cache-only");
var createStringForHash = (optimizedProperties) => `${optimizedProperties.databaseId}${optimizedProperties.password}${optimizedProperties.serviceLoginId}${optimizedProperties.sessionId}${optimizedProperties.userId}${JSON.stringify(optimizedProperties.clientOptions ?? "")}`;

// src/createAuthenticatedRecommendationClient.ts
var LOCAL_STORAGE_DEFAULT_KEY = "beam-react-recommendation-client-auth-cache";
var MAX_STORE_SIZE = 25;
var MIN_TOKEN_LIFETIME = minutesToMilliseconds(5);
var CLOUDFLARE_WORKER_MAX_RUNTIME = secondsToMilliseconds(30);
var store = {};
var lifetimes = [];
async function createAuthenticatedRecommendationClient(optimizedProperties) {
  pruneCredentialsPastTimeToLive();
  const credentialHash = md5(
    createStringForHash(optimizedProperties)
  );
  const localStorageKey = optimizedProperties.authLocalStorageKey ?? LOCAL_STORAGE_DEFAULT_KEY;
  restoreStoreValueFromLocalStorage({
    credentialHash,
    localStorageKey
  });
  const initialStoreValue = store[credentialHash];
  if (initialStoreValue instanceof Promise) {
    await initialStoreValue;
  }
  const currentStoreValue = store[credentialHash];
  const initialCredentials = !(currentStoreValue instanceof Promise) ? currentStoreValue : void 0;
  const client = new RecommendationClient({
    ...optimizedProperties.clientOptions,
    initialCredentials,
    allowedAccountRoles: optimizedProperties.clientOptions?.allowedAccountRoles ?? "frontend"
  });
  if (initialCredentials) {
    return client;
  }
  const loginPromise = client.send(new LoginServiceCommand(optimizedProperties));
  store[credentialHash] = loginPromise;
  await loginPromise;
  const nextInitialCredentials = client.getCredentials();
  updateStore({
    nextInitialCredentials,
    credentialHash
  });
  if (localStorageKey !== MEMORY_CACHE_ONLY && isLocalStorageAvailable()) {
    globalThis.localStorage.setItem(
      localStorageKey,
      JSON.stringify({ [credentialHash]: nextInitialCredentials })
    );
  }
  return client;
}
function restoreStoreValueFromLocalStorage({
  credentialHash,
  localStorageKey
}) {
  if (localStorageKey !== MEMORY_CACHE_ONLY && !store[credentialHash] && isLocalStorageAvailable()) {
    const storedCredentials = localStorage.getItem(localStorageKey);
    if (storedCredentials) {
      const parsedCredentials = run(() => {
        try {
          return JSON.parse(storedCredentials);
        } catch {
          return void 0;
        }
      });
      if (parsedCredentials[credentialHash]) {
        const nextInitialCredentials = run(() => {
          try {
            return r.required(
              r.object(
                parsedCredentials[credentialHash],
                ({
                  currentDatabaseId,
                  serviceLoginId,
                  refreshToken,
                  refreshTokenExpectedExpiration,
                  token,
                  tokenExpectedExpiration
                }) => ({
                  currentDatabaseId: r.required(r.string(currentDatabaseId)),
                  serviceLoginId: r.required(r.string(serviceLoginId)),
                  refreshToken: r.required(r.string(refreshToken)),
                  refreshTokenExpectedExpiration: r.required(
                    r.date(refreshTokenExpectedExpiration)
                  ),
                  token: r.required(r.string(token)),
                  tokenExpectedExpiration: r.required(
                    r.date(tokenExpectedExpiration)
                  )
                })
              )
            );
          } catch {
            return void 0;
          }
        });
        const isUpdateSuccessful = run(() => {
          if (!nextInitialCredentials) {
            return false;
          }
          return updateStore({
            credentialHash,
            nextInitialCredentials
          });
        });
        if (!isUpdateSuccessful) {
          localStorage.removeItem(localStorageKey);
        }
      }
    }
  }
}
function updateStore({
  nextInitialCredentials,
  credentialHash
}) {
  const nextTokenExpectedExpiration = nextInitialCredentials.tokenExpectedExpiration?.getTime();
  if (nextInitialCredentials.token && !nextTokenExpectedExpiration) {
    store[credentialHash] = nextInitialCredentials;
    lifetimes.push([
      credentialHash,
      Date.now() + MIN_TOKEN_LIFETIME + CLOUDFLARE_WORKER_MAX_RUNTIME
    ]);
    if (lifetimes.length >= MAX_STORE_SIZE) {
      removeStoreValue(0, store, lifetimes);
    }
    return true;
  }
  if (nextInitialCredentials.token && nextTokenExpectedExpiration && nextTokenExpectedExpiration > getAllowableExpiration()) {
    store[credentialHash] = nextInitialCredentials;
    lifetimes.push([credentialHash, nextTokenExpectedExpiration]);
    if (lifetimes.length >= MAX_STORE_SIZE) {
      removeStoreValue(0, store, lifetimes);
    }
    return true;
  }
  return false;
}
function removeStoreValue(credentialHashOrLifetimeIndex, storeInstance, lifetimesInstance) {
  if (typeof credentialHashOrLifetimeIndex === "number") {
    const lifetimeIndex = credentialHashOrLifetimeIndex;
    const [credentialHash] = lifetimesInstance[lifetimeIndex] || [];
    if (credentialHash) {
      lifetimesInstance.splice(lifetimeIndex, 1);
      delete storeInstance[credentialHash];
    }
  } else {
    const credentialHash = credentialHashOrLifetimeIndex;
    const foundLifetimeIndex = lifetimesInstance.findIndex(
      ([hash]) => hash === credentialHash
    );
    lifetimesInstance.splice(foundLifetimeIndex, 1);
    delete storeInstance[credentialHash];
  }
}
function getAllowableExpiration() {
  return Date.now() + MIN_TOKEN_LIFETIME;
}
function pruneCredentialsPastTimeToLive() {
  if (isAccurateDateAvailable) {
    const allowableExpiration = getAllowableExpiration();
    const firstValidIndex = lifetimes.findIndex(
      (entry) => entry[1] > allowableExpiration
    );
    if (firstValidIndex === 0) {
      return;
    }
    if (firstValidIndex === -1) {
      lifetimes.forEach(([credentialHash]) => {
        removeStoreValue(credentialHash, store, lifetimes);
      });
      return;
    }
    const copyOfLifetimes = [...lifetimes];
    copyOfLifetimes.slice(0, firstValidIndex).forEach(([credentialHash]) => {
      removeStoreValue(credentialHash, store, lifetimes);
    });
    return;
  }
  return;
}
function isLocalStorageAvailable() {
  return typeof globalThis !== "undefined" && "localStorage" in globalThis;
}

// src/utils/scenarios.ts
var SCENARIO_OMITTED = Symbol("SCENARIO_OMITTED");
function simplifyScenario(scenario) {
  return scenario === SCENARIO_OMITTED ? void 0 : scenario;
}

// src/utils/devMessage.ts
var PREFIX = "[beam-react]";
function devMessage(message, options) {
  console[options?.level || "warn"](
    `${options?.prefix ?? PREFIX, message} ${message}`
  );
}

// src/utils/detectRunMode.ts
function detectRunMode() {
  if (typeof window === "undefined") {
    return "server";
  }
  if (typeof window === "object") {
    return "browser";
  }
  return "unknown";
}
var detectedRunMode = detectRunMode();

// src/getItemBasedRecommendations.ts
async function getItemBasedRecommendations(props) {
  const {
    clientOptions,
    databaseId,
    itemId,
    password,
    serviceLoginId,
    maxResults,
    cursor,
    filters,
    reranking,
    scenario,
    skipDefaultScenario,
    sessionId,
    userId
  } = props;
  const apiOptions = {
    maxResults,
    cursor,
    filters,
    reranking,
    skipDefaultScenario
  };
  const userOrSessionId = run2(
    () => {
      if (userId) {
        return { userId };
      }
      if (sessionId) {
        return { sessionId };
      }
      throw Error(
        "[getItemBasedRecommendations] Either a userId or a sessionId must be provided."
      );
    }
  );
  const client = await createAuthenticatedRecommendationClient({
    ...clientOptions,
    databaseId,
    password,
    serviceLoginId,
    ...userOrSessionId
  });
  return await client.send(
    new GetRelatedItemRecommendationsCommand({
      ...apiOptions,
      itemId,
      scenario: scenario === SCENARIO_OMITTED ? void 0 : scenario,
      ...userOrSessionId
    })
  );
}

// src/getPersonalizedRecommendations.ts
import {
  GetSessionBasedItemRecommendationsCommand,
  GetUserBasedItemRecommendationsCommand,
  GetSessionWithContextItemBasedItemRecommendationsCommand,
  GetUserWithContextItemBasedItemRecommendationsCommand
} from "@crossingminds/recommendation-client";
async function getPersonalizedRecommendations(props) {
  const {
    authLocalStorageKey,
    clientOptions,
    contextItems,
    cursor,
    databaseId,
    excludeRatedItems,
    filters,
    maxResults,
    password,
    reranking,
    serviceLoginId,
    sessionId,
    sessionScenario,
    sessionWithContextScenario,
    skipDefaultScenario,
    userId,
    userScenario,
    userWithContextScenario
  } = props;
  if (userId) {
    if (contextItems && userWithContextScenario) {
      const contextClient = await createAuthenticatedRecommendationClient({
        authLocalStorageKey,
        clientOptions,
        databaseId,
        password,
        serviceLoginId,
        userId
      });
      return await contextClient.send(
        new GetUserWithContextItemBasedItemRecommendationsCommand({
          contextItems,
          cursor,
          excludeRatedItems,
          filters,
          maxResults,
          reranking,
          scenario: simplifyScenario(userWithContextScenario),
          skipDefaultScenario,
          userId
        })
      );
    }
    if (contextItems) {
      devMessage(
        "Recommendations are not working as expected! contextItems were provided but not used because no userWithContextScenario was provided. Add a userWithContextScenario or use the value SCENARIO_OMITTED to use the default."
      );
    }
    if (userScenario || !userScenario && !sessionId) {
      if (!userScenario) {
        devMessage(
          "No userScenario was provided. Add a userScenario or use the value SCENARIO_OMITTED to use the default."
        );
      }
      const client = await createAuthenticatedRecommendationClient({
        authLocalStorageKey,
        clientOptions,
        databaseId,
        password,
        serviceLoginId,
        userId
      });
      return await client.send(
        new GetUserBasedItemRecommendationsCommand({
          cursor,
          excludeRatedItems,
          filters,
          maxResults,
          reranking,
          scenario: simplifyScenario(userScenario),
          skipDefaultScenario,
          userId
        })
      );
    }
  }
  if (sessionId) {
    if (userId) {
      devMessage(
        "Recommendations are not working as expected! userId was provided but not used because one of the following: (1) no userScenario was provided (2) no userWithContextScenario with contextItems were provided."
      );
    }
    const client = await createAuthenticatedRecommendationClient({
      authLocalStorageKey,
      clientOptions,
      databaseId,
      password,
      serviceLoginId,
      sessionId
    });
    if (contextItems && sessionWithContextScenario) {
      return await client.send(
        new GetSessionWithContextItemBasedItemRecommendationsCommand({
          contextItems,
          cursor,
          excludeRatedItems,
          filters,
          maxResults,
          reranking,
          scenario: simplifyScenario(sessionWithContextScenario),
          sessionId,
          skipDefaultScenario
        })
      );
    }
    if (contextItems) {
      devMessage(
        "Recommendations are not working as expected! contextItems were provided but not used because no sessionWithContextScenario was provided. Add a sessionWithContextScenario or use the value SCENARIO_OMITTED to use the default."
      );
    }
    return await client.send(
      new GetSessionBasedItemRecommendationsCommand({
        cursor,
        excludeRatedItems,
        filters,
        maxResults,
        reranking,
        scenario: simplifyScenario(sessionScenario),
        sessionId,
        skipDefaultScenario
      })
    );
  }
  throw new Error("Either userId or sessionId must be provided.");
}

// src/getPropertyRecommendations.ts
import {
  GetSessionBasedItemPropertyRecommendationsCommand,
  GetUserBasedPropertyRecommendationsCommand
} from "@crossingminds/recommendation-client";
async function getPropertyRecommendations({
  databaseId,
  password,
  propertyName,
  serviceLoginId,
  authLocalStorageKey,
  clientOptions,
  filters,
  maxResults,
  sessionId,
  sessionPropertiesScenario,
  userId,
  userPropertiesScenario
}) {
  if (userId) {
    if (!userPropertiesScenario) {
      devMessage("userScenario is required when userId is provided");
    }
    const client = await createAuthenticatedRecommendationClient({
      databaseId,
      password,
      serviceLoginId,
      authLocalStorageKey,
      clientOptions,
      userId
    });
    return client.send(
      new GetUserBasedPropertyRecommendationsCommand({
        filters,
        maxResults,
        propertyName,
        scenario: simplifyScenario(userPropertiesScenario),
        userId
      })
    );
  }
  if (sessionId) {
    if (!sessionPropertiesScenario) {
      devMessage("sessionScenario is required when sessionId is provided");
    }
    const client = await createAuthenticatedRecommendationClient({
      databaseId,
      password,
      serviceLoginId,
      authLocalStorageKey,
      clientOptions,
      sessionId
    });
    return client.send(
      new GetSessionBasedItemPropertyRecommendationsCommand({
        filters,
        maxResults,
        propertyName,
        scenario: simplifyScenario(sessionPropertiesScenario),
        sessionId
      })
    );
  }
  throw Error("userId or sessionId is required");
}

// src/getPrecomputedItemBasedRecommendations.ts
import { GetPrecomputedRelatedItemRecommendationsCommand } from "@crossingminds/recommendation-client";
import { run as run3 } from "@crossingminds/utils";
async function getPrecomputedItemBasedRecommendations(props) {
  const {
    clientOptions,
    databaseId,
    itemId,
    password,
    serviceLoginId,
    maxResults,
    scenario,
    skipDefaultScenario,
    sessionId,
    userId
  } = props;
  const apiOptions = {
    maxResults,
    skipDefaultScenario
  };
  const userOrSessionId = run3(
    () => {
      if (userId) {
        return { userId };
      }
      if (sessionId) {
        return { sessionId };
      }
      throw Error(
        "[GetPrecomputedItemBasedRecommendations] Either a userId or a sessionId must be provided."
      );
    }
  );
  const client = await createAuthenticatedRecommendationClient({
    ...clientOptions,
    databaseId,
    password,
    serviceLoginId,
    ...userOrSessionId
  });
  return await client.send(
    new GetPrecomputedRelatedItemRecommendationsCommand({
      ...apiOptions,
      itemId,
      scenario,
      ...userOrSessionId
    })
  );
}

// src/getSessionBasedRecommendations.ts
import {
  RecommendationClient as RecommendationClient2,
  GetSessionBasedItemRecommendationsCommand as GetSessionBasedItemRecommendationsCommand2,
  LoginServiceCommand as LoginServiceCommand2
} from "@crossingminds/recommendation-client";
async function getSessionBasedRecommendations({
  clientOptions,
  databaseId,
  password,
  serviceLoginId,
  sessionId,
  getSessionBasedItemRecommendationsOptions
}) {
  const client = new RecommendationClient2({
    ...clientOptions,
    allowedAccountRoles: clientOptions?.allowedAccountRoles || "frontend"
  });
  await client.send(
    new LoginServiceCommand2({
      databaseId,
      password,
      serviceLoginId,
      sessionId
    })
  );
  return await client.send(
    new GetSessionBasedItemRecommendationsCommand2({
      ...getSessionBasedItemRecommendationsOptions,
      sessionId
    })
  );
}

// src/getUserBasedRecommendations.ts
import {
  RecommendationClient as RecommendationClient3,
  GetUserBasedItemRecommendationsCommand as GetUserBasedItemRecommendationsCommand2,
  LoginServiceCommand as LoginServiceCommand3
} from "@crossingminds/recommendation-client";
async function getUserBasedRecommendations({
  clientOptions,
  databaseId,
  password,
  serviceLoginId,
  sessionId,
  userId,
  getUserBasedItemRecommendationsOptions
}) {
  const client = new RecommendationClient3({
    ...clientOptions,
    allowedAccountRoles: clientOptions?.allowedAccountRoles || "frontend"
  });
  await client.send(
    new LoginServiceCommand3({
      databaseId,
      password,
      serviceLoginId,
      userId
    })
  );
  return await client.send(
    new GetUserBasedItemRecommendationsCommand2({
      ...getUserBasedItemRecommendationsOptions,
      userId,
      sessionId
    })
  );
}

// src/resolveSessionId.ts
import {
  LoginServiceCommand as LoginServiceCommand4,
  RecommendationClient as RecommendationClient4
} from "@crossingminds/recommendation-client";
async function resolveSessionId({
  databaseId,
  password,
  serviceLoginId,
  sessionId,
  userId,
  clientOptions
}) {
  if (!sessionId || !userId) {
    console.error(
      "[resolveSessionId] sessionId and userId are required to resolve session"
    );
    return false;
  }
  try {
    const client = new RecommendationClient4({
      ...clientOptions,
      allowedAccountRoles: clientOptions?.allowedAccountRoles || "frontend"
    });
    await client.send(
      new LoginServiceCommand4({
        databaseId,
        password,
        serviceLoginId,
        sessionId,
        userId
      })
    );
    return true;
  } catch (error) {
    console.error(error);
  }
  console.error("[resolveSessionId] failed to resolve session");
  return false;
}

// src/useRecordItemInteractions.ts
import { useMemo, useRef } from "react";
import {
  CreateOneAnonymousSessionInteractionCommand,
  CreateOneUserInteractionCommand
} from "@crossingminds/recommendation-client";
import { isStringGuard as isStringGuard2 } from "@crossingminds/utils";
function useRecordItemInteractions(props) {
  const {
    databaseId,
    password,
    serviceLoginId,
    clientOptions,
    authLocalStorageKey,
    runMode = "browser"
  } = props;
  const sessionId = props.sessionId;
  const userId = props.userId;
  const authenticatedClient = useRef(
    void 0
  );
  return useMemo(() => {
    switch (runMode) {
      case "always":
        break;
      case "never":
        return buildDummyMethods({ runMode });
      case "server":
        switch (detectedRunMode) {
          case "browser":
            return buildDummyMethods({ runMode });
          case "server":
            break;
          case "unknown":
            break;
        }
        break;
      case "browser": {
        switch (detectedRunMode) {
          case "browser":
            break;
          case "server":
            return buildDummyMethods({ runMode });
          case "unknown":
            break;
        }
        break;
      }
    }
    authenticatedClient.current = void 0;
    if (isStringGuard2(userId)) {
      const clientPromise = createAuthenticatedRecommendationClient({
        clientOptions,
        databaseId,
        password,
        serviceLoginId,
        userId,
        authLocalStorageKey
      });
      clientPromise.then((client) => {
        authenticatedClient.current = client;
      });
      const sendUserInteraction = (
        /* no async! */
        ({
          itemId,
          interactionType,
          properties
        }) => {
          const command = new CreateOneUserInteractionCommand({
            interactionType,
            itemId,
            userId,
            timestamp: /* @__PURE__ */ new Date(),
            properties
          });
          if (authenticatedClient.current) {
            authenticatedClient.current.send(command);
          } else {
            clientPromise.then((client) => {
              client.send(command);
            });
          }
        }
      );
      const recordItemClickInteraction = async (itemId) => {
        sendUserInteraction({ interactionType: "rw_product_click", itemId });
      };
      const recordItemPageViewInteraction = async (itemId) => {
        sendUserInteraction({ interactionType: "product_page_view", itemId });
      };
      const recordAddItemToCartInteraction = async (itemId) => {
        sendUserInteraction({ interactionType: "add_to_cart", itemId });
      };
      const recordItemTransactionCompleteInteraction = (itemId) => {
        sendUserInteraction({ interactionType: "paid", itemId });
      };
      const recordCustomItemInteraction = (param) => {
        sendUserInteraction(param);
      };
      return {
        recordCustomItemInteraction,
        recordItemClickInteraction,
        recordItemPageViewInteraction,
        recordItemTransactionCompleteInteraction,
        recordAddItemToCartInteraction
      };
    }
    if (isStringGuard2(sessionId)) {
      const clientPromise = createAuthenticatedRecommendationClient({
        clientOptions,
        databaseId,
        password,
        serviceLoginId,
        sessionId,
        authLocalStorageKey
      });
      clientPromise.then((client) => {
        authenticatedClient.current = client;
      });
      const sendSessionInteraction = (
        /* no async! */
        ({
          itemId,
          interactionType,
          properties
        }) => {
          const command = new CreateOneAnonymousSessionInteractionCommand({
            interactionType,
            itemId,
            sessionId,
            timestamp: /* @__PURE__ */ new Date(),
            properties
          });
          if (authenticatedClient.current) {
            authenticatedClient.current.send(command);
          } else {
            clientPromise.then((client) => {
              client.send(command);
            });
          }
        }
      );
      const recordItemClickInteraction = async (itemId) => {
        sendSessionInteraction({ interactionType: "rw_product_click", itemId });
      };
      const recordItemPageViewInteraction = async (itemId) => {
        sendSessionInteraction({ interactionType: "product_page_view", itemId });
      };
      const recordAddItemToCartInteraction = async (itemId) => {
        sendSessionInteraction({ interactionType: "add_to_cart", itemId });
      };
      const recordItemTransactionCompleteInteraction = async (itemId) => {
        sendSessionInteraction({ interactionType: "paid", itemId });
      };
      const recordCustomItemInteraction = async (param) => {
        sendSessionInteraction(param);
      };
      return {
        recordCustomItemInteraction,
        recordItemClickInteraction,
        recordItemPageViewInteraction,
        recordItemTransactionCompleteInteraction,
        recordAddItemToCartInteraction
      };
    }
    console.error(
      "[useRecordItemInteractions] Either sessionId or userId must be provided"
    );
    return buildDummyMethods({ runMode });
  }, [
    authLocalStorageKey,
    clientOptions,
    databaseId,
    password,
    runMode,
    serviceLoginId,
    sessionId,
    userId
  ]);
}
function buildDummyMethods({
  runMode
}) {
  const dummy = () => {
    console.error(
      `[useRecordItemInteractions] Either sessionId or userId must be provided. Interaction has not been recorded. This has serious implications for user experience. \`runMode\` is set to \`${runMode}\`. Detected mode is \`${detectedRunMode}\`. If there is a mismatch, you should set the run mode manually with \`always\` and \`never\` values.`
    );
    return Promise.resolve();
  };
  return {
    recordCustomItemInteraction: dummy,
    recordItemClickInteraction: dummy,
    recordItemPageViewInteraction: dummy,
    recordItemTransactionCompleteInteraction: dummy,
    recordAddItemToCartInteraction: dummy
  };
}
export {
  AccessSessionId,
  SCENARIO_OMITTED,
  getItemBasedRecommendations,
  getPersonalizedRecommendations,
  getPrecomputedItemBasedRecommendations,
  getPropertyRecommendations,
  getSessionBasedRecommendations,
  getUserBasedRecommendations,
  resolveSessionId,
  useRecordItemInteractions
};
